import React, { useState } from "react";
import { Card, Descriptions, Tag, Collapse, Modal, Button, Space, Typography, notification } from "antd";
import { useCustomMutation } from "@refinedev/core";
import { DeleteOutlined, EyeOutlined, ArrowLeftOutlined, DownloadOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DELETE_PROCUREMENT_MINUTE_MUTATION, GENERATE_MINUTE_DOCUMENT_MUTATION, UPDATE_PROCUREMENT_MINUTE_MUTATION } from "../queries";
import { MarkdownEditor } from "../../../components/MarkdownEditor";

const { Panel } = Collapse;
const { Title } = Typography;

interface IMinutesTabProps {
  minutes: any[];
  onRefetch?: () => void;
}

export const MinutesTab: React.FC<IMinutesTabProps> = ({ minutes, onRefetch }) => {
  const [selectedMinute, setSelectedMinute] = useState<any | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [minuteToDelete, setMinuteToDelete] = useState<any | null>(null);

  const { mutate: deleteMinute, isLoading: isDeleting } = useCustomMutation();
  const { mutate: generateMinuteDocument, isLoading: isGenerating } = useCustomMutation();
  const { mutate: updateMinute, isLoading: isUpdating } = useCustomMutation();

  const handleViewDetails = (minute: any) => {
    setSelectedMinute(minute);
  };

  const handleBackToList = () => {
    setSelectedMinute(null);
  };

  const handleDeleteClick = (minute: any) => {
    setMinuteToDelete(minute);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (minuteToDelete) {
      deleteMinute({
        meta: {
          gqlMutation: DELETE_PROCUREMENT_MINUTE_MUTATION,
        },
        values: { id: minuteToDelete.id },
        url: "",
        method: "post"
      }, {
        onSuccess: () => {
          notification.success({
            message: "Minute deleted successfully",
            description: "The procurement minute has been deleted.",
          });
          setDeleteModalVisible(false);
          setMinuteToDelete(null);
          onRefetch?.();
        },
        onError: (error) => {
          notification.error({
            message: "Failed to delete minute",
            description: error?.response?.errors?.[0]?.message || "An error occurred while deleting the minute.",
          });
          console.error("Delete error:", error);
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setMinuteToDelete(null);
  };

  const handleLockClick = () => {
    if (!selectedMinute) return;

    updateMinute({
      meta: {
        gqlMutation: UPDATE_PROCUREMENT_MINUTE_MUTATION,
      },
      values: { id: selectedMinute.id, input: { locked: true } },
      url: "",
      method: "post"
    }, {
      onSuccess: (response: any) => {
        notification.success({
          message: "Minute locked successfully",
          description: "The procurement minute has been locked and can no longer be edited or deleted.",
        });
        // Update the selected minute with the locked status
        setSelectedMinute(response?.data?.updateProcurementMinute);
        onRefetch?.();
      },
      onError: (error) => {
        notification.error({
          message: "Failed to lock minute",
          description: error?.response?.errors?.[0]?.message || "An error occurred while locking the minute.",
        });
        console.error("Lock error:", error);
      },
    });
  };

  const handleSaveContent = (newContent: string) => {
    if (!selectedMinute) return;

    updateMinute({
      meta: {
        gqlMutation: UPDATE_PROCUREMENT_MINUTE_MUTATION,
      },
      values: { id: selectedMinute.id, input: { content: newContent } },
      url: "",
      method: "post"
    }, {
      onSuccess: (response: any) => {
        notification.success({
          message: "Content saved successfully",
          description: "The minute content has been updated.",
        });
        // Update the selected minute with the new content
        setSelectedMinute(response?.data?.updateProcurementMinute);
        onRefetch?.();
      },
      onError: (error) => {
        notification.error({
          message: "Failed to save content",
          description: error?.response?.errors?.[0]?.message || "An error occurred while saving the content.",
        });
        console.error("Save error:", error);
      },
    });
  };

  const handleGenerateDocument = () => {
    if (!selectedMinute) return;

    generateMinuteDocument(
      {
        url: "",
        method: "post",
        values: {
          minuteId: selectedMinute.id,
        },
        meta: { gqlMutation: GENERATE_MINUTE_DOCUMENT_MUTATION },
      },
      {
        onSuccess: (response: any) => {
          notification.success({
            message: 'Document Generated',
            description: 'Minute document has been generated successfully.',
          });
          // Update the selected minute with the new rendered snapshot
          setSelectedMinute(response?.data?.generateMinuteDocument);
          onRefetch?.();
        },
        onError: (error: any) => {
          notification.error({
            message: 'Generation Failed',
            description: error.message || 'Failed to generate minute document.',
          });
        },
      },
    );
  };

  if (minutes.length === 0) {
    return (
      <Card title="Procurement Minutes">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>No procurement minutes available for this sourcing.</p>
        </div>
      </Card>
    );
  }

  // Detail view
  if (selectedMinute) {
    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            Back to List
          </Button>
        </Space>

        <Card title={`Minute Details - ${selectedMinute.minuteTitle}`}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Minute Type">
              <Tag color={selectedMinute.minuteType === "BID_OPENING" ? "blue" : "green"}>
                {selectedMinute.minuteType.replace("_", " ")}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Title">
              {selectedMinute.minuteTitle}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  selectedMinute.status === "DRAFT"
                    ? "orange"
                    : selectedMinute.status === "SUBMITTED"
                    ? "blue"
                    : "green"
                }
              >
                {selectedMinute.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Locked">
              <Tag color={selectedMinute.locked ? "red" : "green"}>
                {selectedMinute.locked ? "Locked" : "Editable"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Prepared By">
              {selectedMinute.preparedBy || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Prepared At">
              {selectedMinute.preparedAt
                ? dayjs(selectedMinute.preparedAt).format("YYYY-MM-DD HH:mm")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Approved By">
              {selectedMinute.approvedBy || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Approved At">
              {selectedMinute.approvedAt
                ? dayjs(selectedMinute.approvedAt).format("YYYY-MM-DD HH:mm")
                : "N/A"}
            </Descriptions.Item>
          </Descriptions>

          {/* Content Section - Shows editor for RAW_CONTENT minutes (read-only when locked) */}
          {selectedMinute.contentType === 'RAW_CONTENT' ? (
            <div style={{ marginTop: 24 }}>
              <MarkdownEditor
             
                key={selectedMinute.id}
                title={selectedMinute.locked ? "Minute Content (Locked)" : "Edit Minute Content"}
                initialMarkdown={selectedMinute.renderedSnapshot || ""}
                onSave={selectedMinute.locked ? undefined : handleSaveContent}
                disabled={selectedMinute.locked}
                isSaving={isUpdating}
                height={500}
                placeholder="Enter minute content in markdown format..."
                showSaveButton={!selectedMinute.locked}
              />
            </div>
          ) : selectedMinute.renderedSnapshot && (
            <div style={{ marginTop: 24 }}>
              <Title level={4}>
                {selectedMinute.locked ? "Rendered Snapshot (Locked)" : "Rendered Snapshot"}
              </Title>
              <div
                style={{
                  background: "#f9fafb",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                }}
                dangerouslySetInnerHTML={{
                  __html: selectedMinute.renderedSnapshot,
                }}
              />
            </div>
          )}

          {selectedMinute.inputs && (
            <div style={{ marginTop: 16 }}>
              <Collapse defaultActiveKey={[]} bordered={false}>
                <Panel header="Inputs" key="inputs">
                  <pre style={{
                    margin: 0,
                    padding: 16,
                    background: "transparent",
                    overflow: "auto"
                  }}>
                    {JSON.stringify(selectedMinute.inputs, null, 2)}
                  </pre>
                </Panel>
              </Collapse>
            </div>
          )}

          {(selectedMinute.contentType === 'DOCUMENT_TEMPLATE' || selectedMinute.contentType === 'RAW_CONTENT') && !selectedMinute.generatedDocument && (
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleGenerateDocument}
                loading={isGenerating}
              >
                Generate Minute Document
              </Button>
            </div>
          )}

          {selectedMinute.generatedDocument && (
            <div style={{ marginTop: 16 }}>
              <Card title="Generated Minute Document" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="File Name">{selectedMinute.generatedDocument.originalName}</Descriptions.Item>
                    <Descriptions.Item label="Size">{(selectedMinute.generatedDocument.size / 1024).toFixed(2)} KB</Descriptions.Item>
                    <Descriptions.Item label="Created By">{selectedMinute.generatedDocument.createdBy || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Category">{selectedMinute.generatedDocument.category || 'N/A'}</Descriptions.Item>
                  </Descriptions>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    href={selectedMinute.generatedDocument.url}
                    target="_blank"
                    size="large"
                  >
                    Download Minute Document
                  </Button>
                </Space>
              </Card>
            </div>
          )}

          {!selectedMinute.locked && (
            <div style={{ marginTop: 16 }}>
              <Space>
                <Button
                  type="default"
                  icon={<LockOutlined />}
                  onClick={handleLockClick}
                  loading={isUpdating}
                >
                  Lock Minute
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteClick(selectedMinute)}
                >
                  Delete Minute
                </Button>
              </Space>
            </div>
          )}
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal
          title="Delete Minute"
          open={deleteModalVisible}
          onOk={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          confirmLoading={isDeleting}
          okText="Delete"
          okType="danger"
        >
          <p>Are you sure you want to delete the minute "{minuteToDelete?.minuteTitle}"?</p>
          <p>This action cannot be undone.</p>
        </Modal>
      </div>
    );
  }

  // List view - Cards
  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Procurement Minutes</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
        {minutes.map((minute) => (
          <Card
            key={minute.id}
            title={minute.minuteTitle}
            extra={
              <Space>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewDetails(minute)}
                >
                  View
                </Button>
                {!minute.locked && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteClick(minute)}
                  >
                    Delete
                  </Button>
                )}
              </Space>
            }
            style={{ height: 'fit-content' }}
          >
            <Space direction="vertical" size="small">
              <div>
                <strong>Type:</strong>{" "}
                <Tag color={minute.minuteType === "BID_OPENING" ? "blue" : "green"}>
                  {minute.minuteType.replace("_", " ")}
                </Tag>
              </div>
              <div>
                <strong>Status:</strong>{" "}
                <Tag
                  color={
                    minute.status === "DRAFT"
                      ? "orange"
                      : minute.status === "SUBMITTED"
                      ? "blue"
                      : "green"
                  }
                >
                  {minute.status}
                </Tag>
              </div>
              <div>
                <strong>Locked:</strong>{" "}
                <Tag color={minute.locked ? "red" : "green"}>
                  {minute.locked ? "Locked" : "Editable"}
                </Tag>
              </div>
              <div>
                <strong>Prepared By:</strong> {minute.preparedBy || "N/A"}
              </div>
              <div>
                <strong>Prepared At:</strong>{" "}
                {minute.preparedAt
                  ? dayjs(minute.preparedAt).format("YYYY-MM-DD HH:mm")
                  : "N/A"}
              </div>
            </Space>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Minute"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={isDeleting}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure you want to delete the minute "{minuteToDelete?.minuteTitle}"?</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};
